/**
 * @author      yewon@zumper.com (Yewon Lee)
 * @copyright   Copyright (c) 2018, Zumper
 * @description getSavedSearches action
 */

import { getQueryBounds, isCommonAuthError, isValidBounds } from '@zcode/nr-utils'
import { count, geos, pins, search } from '@zcode/nr/es/shared/api'
import { SavedSearch } from '@zcode/nr/es/shared/bizos/searches'
import { resetClustersAndListables } from '../../../actions/clusters.action'
import { logger } from '../../../logger'
import { clearEntity } from '../../detail/modules/entity/actions/index'
import {
  beginLoadingSavedSearches,
  errorLoadingSavedSearches,
  hideSavedSearchesView,
  receiveAllCounts,
  receiveCounts,
  receiveSavedSearches,
  setAllFilters,
  setImages,
  setOnlyNewFilters,
  showSavedSearchesView,
  updateSavedSearches,
} from '../actions/index'

export const getAllQueries = (savedSearches, newOnly) => {
  if (newOnly) {
    return savedSearches.map(({ query, viewedOn }) => {
      return { ...query, minListedOn: viewedOn }
    })
  }
  return savedSearches.map(({ query }) => query)
}

const getSavedSearchesCount = (savedSearches, newOnly) => (dispatch) => {
  const queries = getAllQueries(savedSearches, newOnly)
  // get count for newly listed listings
  count
    .query({
      queries,
      all: false,
      per: true,
      sort: ['newest'],
      P: null,
    })
    .then((counts) => {
      if (newOnly) {
        dispatch(receiveCounts({ counts: counts.queries }))
      } else {
        dispatch(receiveAllCounts({ counts: counts.queries }))
      }
    })
    .catch((err) => {
      if (!isCommonAuthError(err)) {
        logger.error('getSavedSearchesCount failed', err)
      }
    })
}

const getSavedSearchesImages = (savedSearches) => (dispatch) => {
  const images = {}
  savedSearches.forEach(({ query, searchId }, idx) => {
    // we only need 4 images per savedSearch Card
    pins
      .query({ ...query, limit: 4 })
      .then((result) => {
        if (result) {
          images[searchId] = result.map(({ mainImage }) => mainImage)
        }

        if (idx === savedSearches.length - 1) {
          dispatch(setImages({ images }))
        }
      })
      .catch((e) => {
        if (!isCommonAuthError(e)) {
          logger.error('Failed to get saved Search images', e.data || e)
        }
      })
  })
}

// some of the old saved-search from DORA doesn't have bounding box defined, this will fill-in the
// missing bounding box associated wit the "url" field of a savedSearch
const fillInMissingBoundingBox = (savedSearches) => (dispatch) => {
  savedSearches.forEach(({ query, searchId }, idx) => {
    const hasValidBounds = isValidBounds(query)
    if (!hasValidBounds) {
      const { url } = query
      // if url starts with pros/ then it's a search based on a pro user, don't need bounding box.
      if (url && !url.startsWith('pros/')) {
        geos
          .get({ url })
          .then((geos) => {
            const location =
              geos.city ||
              (geos.nearbyCities && geos.nearbyCities[0]) ||
              geos.hood ||
              (geos.nearbyHoods && geos.nearbyHoods[0])
            if (location && location.box) {
              const box = getQueryBounds(location.box)
              const target = savedSearches[idx].toJSON()

              target.query = { ...target.query, ...box }
              const updated = [...savedSearches]
              updated[idx] = SavedSearch.fromJS(target)

              dispatch(updateSavedSearches({ savedSearches: updated }))
              dispatch(setAllFilters({ filters: getAllQueries(updated) }))
              dispatch(setOnlyNewFilters({ filters: getAllQueries(updated, true) }))
            }
          })
          .catch((error) => {
            if (!isCommonAuthError(error)) {
              logger.error(url, error, new Error('fillInMissingBoundingBox: geos call failed'))
            }
          })
      }
    }
  })
}

const loadSavedSearches = () => (dispatch) => {
  dispatch(beginLoadingSavedSearches())
  return search
    .get()
    .then((savedSearches) => {
      dispatch(receiveSavedSearches({ savedSearches }))
      if (savedSearches) {
        if (savedSearches.length) {
          dispatch(resetClustersAndListables())
          dispatch(clearEntity())
          dispatch(showSavedSearchesView())
          // get images for savesSearches
          dispatch(getSavedSearchesImages(savedSearches))
          // get count for newly listed listings
          dispatch(getSavedSearchesCount(savedSearches))
          // get count for all listings
          dispatch(getSavedSearchesCount(savedSearches, true))
          // save all alerts filters
          dispatch(setAllFilters({ filters: getAllQueries(savedSearches) }))
          // save all new alerts filters
          dispatch(setOnlyNewFilters({ filters: getAllQueries(savedSearches, true) }))
          // fill-in missing bounding box of a savedSearch
          dispatch(fillInMissingBoundingBox(savedSearches))
        } else {
          dispatch(hideSavedSearchesView())
        }
      }
      return savedSearches
    })
    .catch((error) => {
      dispatch(errorLoadingSavedSearches(error))
      dispatch(hideSavedSearchesView())
      return Promise.reject(error)
    })
}

export default loadSavedSearches
